import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { capitalise } from 'lib/string/stringUtils'
import type { LuxPlusInvalidCardReason } from 'luxPlus/lib/getLuxPlusInvalidCardReason'

const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.messaging.critical.normalForeground};
  border-radius: ${props => props.theme.borderRadius.L};
`

const CardDetails = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${rem(8)};
`

const CardLogo = styled.img`
  max-width: ${rem(24)};
  max-height: ${rem(16)};
`

// Fixed min width with left-aligned content so the icons line up across rows with different reasons
const Warning = styled.div`
  display: flex;
  flex-shrink: 0;
  min-width: ${rem(80)};
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
`

interface Props {
  card: App.StripePaymentCardV2
  isPreferred: boolean
  reason: LuxPlusInvalidCardReason
}

function LuxPlusInvalidCardModalCardRow({ card, isPreferred, reason }: Props) {
  const intl = useIntl()

  const roleLabel = isPreferred ? intl.formatMessage({
    id: 'GUnbcOnZx7jbPufOHJAZ',
    defaultMessage: 'Preferred',
    description: 'Label on the LuxPlus+ invalid card modal row for the card currently used first for renewal',
  }) : intl.formatMessage({
    id: 'am3_bWlLSBi2Xp7LjR2M',
    defaultMessage: 'Backup',
    description: 'Label on the LuxPlus+ invalid card modal row for a backup card used if the preferred renewal card fails',
  })

  const reasonLabel = reason === 'expired' ? intl.formatMessage({
    id: 'NZU4TqlHMHmLclXiUgWq',
    defaultMessage: 'Expired',
    description: 'Status on a card row in the LuxPlus+ invalid card modal when the renewal card has passed its expiry date',
  }) : intl.formatMessage({
    id: 'UC2sX1-Ckx550fqCpLnh',
    defaultMessage: 'Not valid',
    description: 'Status on a card row in the LuxPlus+ invalid card modal when the bank has flagged the renewal card for a reason other than expiry',
  })

  return <CardRow>
    <CardDetails>
      {card.logo && <CardLogo src={card.logo} alt="" />}
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'USEMWCFHetSO4VVbzAyh',
          defaultMessage: '{brand} ******** {last4}',
          description: 'Backup payment card label in LuxPlus+ account management',
        }, { brand: capitalise(card.brand), last4: card.last4 })}
      </BodyText>
      <BodyText variant="small" colour="neutral-two">{roleLabel}</BodyText>
    </CardDetails>
    <Warning>
      <LineInfoCircleIcon size="S" colour="critical" />
      <BodyText variant="small" colour="critical">{reasonLabel}</BodyText>
    </Warning>
  </CardRow>
}

export default LuxPlusInvalidCardModalCardRow
